import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import "../styles/TextReveal.css";

// Renders nothing — finds headings and eyebrow labels on the current
// page and splits each one into per-word spans, then reveals the
// words with a staggered slide-up-through-a-mask animation as they
// scroll into view. Mixed content (e.g. a heading with an <Icon /> or
// a <br /> inside it) is left untouched — only plain text nodes are
// split, so existing markup never breaks.
const SELECTOR = "main h1, main h2, main .tp-section-heading, main .tp-eyebrow";

function splitElementIntoWords(el) {
  if (el.dataset.tpTextSplit === "true") return;
  el.dataset.tpTextSplit = "true";

  const originalNodes = Array.from(el.childNodes);
  const newNodes = [];
  let wordIndex = 0;

  originalNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Split on whitespace but keep the whitespace tokens so word
      // wrapping / spacing stays exactly as the browser would render it.
      const tokens = node.textContent.split(/(\s+)/);
      tokens.forEach((token) => {
        if (token === "") return;
        if (/^\s+$/.test(token)) {
          newNodes.push(document.createTextNode(token));
          return;
        }
        const outer = document.createElement("span");
        outer.className = "tp-text-reveal__word";
        const inner = document.createElement("span");
        inner.className = "tp-text-reveal__word-inner";
        inner.textContent = token;
        inner.style.setProperty(
          "--tp-word-delay",
          `${Math.min(wordIndex, 14) * 45}ms`
        );
        outer.appendChild(inner);
        newNodes.push(outer);
        wordIndex += 1;
      });
    } else {
      // Non-text children (icons, <br>, nested spans, etc.) pass through.
      newNodes.push(node);
    }
  });

  el.textContent = "";
  newNodes.forEach((node) => el.appendChild(node));
}

export default function TextReveal() {
  const location = useLocation();

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const targets = Array.from(document.querySelectorAll(SELECTOR));
    if (targets.length === 0) return undefined;

    targets.forEach((el) => splitElementIntoWords(el));

    if (prefersReducedMotion) {
      targets.forEach((el) => el.classList.add("tp-text-reveal--visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("tp-text-reveal--visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );

    targets.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [location.pathname]);

  return null;
}
